import React from "react";
import {
    Card,
    CardTitle,
    Badge,
    ListGroup,
    ListGroupItem,
    Table,
} from "reactstrap";
import { FormattedMessage } from "react-intl";

import { Localisation } from "@localisation/index";
import { Locale } from "@localisation/models";
import { DataRow, DataRowDate, DataRowTranslated, DataRowBoolean } from "@ui/DataRow";
import { TrackPreviewTable } from "../TrackPreviewTable";
import { Copyright } from "../Copyright";
import { messages } from "../../messages";
import { Album, Artist, Track } from "../../models";

interface AlbumPreviewProps {
    album: Album;
    locale: Locale;
}

export const AlbumPreview = ({ album, locale }: AlbumPreviewProps) => (
    <div className="album-preview">
        {album != null &&
            <Card>
                <ListGroup flush>
                    <ListGroupItem>
                        {album.format != null &&
                            <h5>
                                <Badge color="secondary">
                                    {Localisation.translateMap(album.format.label, locale)}
                                </Badge>
                            </h5>
                        }
                        <CardTitle tag="h4" className="album-preview__title">
                            {album.title}{" "}
                            {album.titleVersion != null && album.titleVersion !== "" &&
                                <Badge color="secondary">{album.titleVersion}</Badge>
                            }
                        </CardTitle>
                        <Table className="mb-0" size="sm" borderless>
                            <tbody>
                                {album.artists != null && (album.artists as Artist[]).map((artist, i) => (
                                    <tr key={i} className={artist.pivot.isMain ? "" : "text-muted"}>
                                        <td>
                                            {artist.name}{" "}
                                            {artist.id == null &&
                                                <Badge color="primary">
                                                    <FormattedMessage {...messages.newArtist} />
                                                </Badge>
                                            }
                                        </td>
                                        <td><em>{Localisation.translateMap(artist.pivot.role.label, locale)}</em></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </ListGroupItem>
                    <ListGroupItem>
                        <DataRow
                            key="upc"
                            message={messages.albumColumnUpc}
                            value={album.upc} />
                        <DataRow
                            key="customId"
                            message={messages.albumColumnCustomId}
                            value={album.customId} />
                        <DataRowTranslated
                            key="genre"
                            message={messages.albumColumnGenre}
                            value={album.genre != null ? album.genre.label : null}
                            locale={locale} />
                        <DataRowTranslated
                            key="additionalGenre"
                            message={messages.albumColumnAdditionalGenre}
                            value={album.additionalGenre != null ? album.additionalGenre.label : null}
                            locale={locale} />
                        <DataRow
                            key="label"
                            message={messages.albumColumnLabel}
                            value={album.label != null ? album.label.name : null} />
                        <DataRowDate
                            key="originalReleaseDate"
                            message={messages.albumColumnOriginalReleaseDate}
                            value={album.originalReleaseDate} />
                        <DataRowDate
                            key="preorderDate"
                            message={messages.albumColumnPreorderDate}
                            value={album.preorderDate} />
                        <DataRowDate
                            key="releaseDate"
                            message={messages.albumColumnReleaseDate}
                            value={album.releaseDate} />
                        <DataRow
                            key="comment"
                            message={messages.albumColumnComment}
                            value={album.comment} />
                        <DataRow
                            key="commentPrivate"
                            message={messages.albumColumnCommentPrivate}
                            value={album.commentPrivate} />
                        <DataRowBoolean
                            key="preorderPreview"
                            message={messages.albumColumnPreorderPreview}
                            value={album.preorderPreview} />
                        <DataRowTranslated
                            key="price"
                            message={messages.albumColumnPrice}
                            value={album.price != null ? album.price.label : null}
                            locale={locale} />
                        <DataRowTranslated
                            key="trackPrice"
                            message={messages.albumColumnTrackPrice}
                            value={album.trackPrice != null ? album.trackPrice.label : null}
                            locale={locale} />
                        <Copyright key="copyright" entity={album} />
                    </ListGroupItem>
                </ListGroup>
                {album.tracks != null &&
                    <TrackPreviewTable album={album} tracks={album.tracks as Track[]} locale={locale} />
                }
            </Card>
        }
    </div>
);
